import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  useTheme,
  Snackbar,
  Avatar,
} from "@mui/material";
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import UserProfile from "../../models/user/UserProfile";
import { ModernTextField } from "../common/StyledComponents";
import { useError } from "../../error/useError";
import ErrorAlert from "../common/ErrorAlert";
import { useUserProfileContext } from "../../contexts/ProfileContext";
import { validateInternationalName } from "../../utils/nameValidation";

const PersonalInfoSection: React.FC = () => {
  const theme = useTheme();
  
  const [editing, setEditing] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const { error, helpText, setError, clearError, handleError } = useError();
  
  // Form fields
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");

  const { userProfile, refetchProfile, updateProfile, isSavingProfile } = useUserProfileContext();
  

  useEffect(() => {
    if (userProfile) {
      setName(userProfile.name);
      setEmail(userProfile.email);
    }
  }, [userProfile]);

  const handleSave = () => {
    const nameError = validateInternationalName(name);
    if (nameError) {
      setError(nameError);
      return;
    }

    const updatedProfile: UserProfile = {
      email: userProfile?.email || email,
      name,
      currency: userProfile?.currency || "USD",
      startingBalance: userProfile?.startingBalance || 0
    };

    updateProfile(updatedProfile, {
      onSuccess: () => {
        setSuccess("Personal information updated successfully!");
        setEditing(false);
        refetchProfile();
      },
      onError: (error) => {
        // Use the backend's user-friendly error message
        handleError(error);
      }
    });
  };

  const handleCancel = () => {
    setEditing(false);
    setName(userProfile?.name || "");
    clearError();
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Avatar
            sx={{
              backgroundColor: theme.palette.primary.main + '20',
              color: theme.palette.primary.main,
            }}
          >
            <PersonIcon />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Personal Information
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage your personal details and profile
            </Typography>
          </Box>
          {!editing ? (
            <Tooltip title="Edit information">
              <IconButton
                onClick={() => setEditing(true)}
                sx={{
                  backgroundColor: theme.palette.primary.main + '10',
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.main + '20',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s'
                }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Save changes">
                <IconButton
                  onClick={handleSave}
                  disabled={isSavingProfile || !name.trim()}
                  sx={{
                    backgroundColor: theme.palette.success.main + '10',
                    color: theme.palette.success.main,
                    '&:hover': {
                      backgroundColor: theme.palette.success.main + '20',
                      transform: 'scale(1.05)',
                    },
                    '&:disabled': {
                      backgroundColor: theme.palette.grey[200],
                      color: theme.palette.grey[400],
                    },
                    transition: 'all 0.2s'
                  }}
                >
                  <SaveIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Cancel">
                <IconButton
                  onClick={handleCancel}
                  sx={{
                    backgroundColor: theme.palette.error.main + '10',
                    color: theme.palette.error.main,
                    '&:hover': {
                      backgroundColor: theme.palette.error.main + '20',
                      transform: 'scale(1.05)',
                    },
                    transition: 'all 0.2s'
                  }}
                >
                  <CancelIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      </Box>

      <ErrorAlert
        error={error}
        helpText={helpText}
        onClear={clearError}
        onRetry={() => refetchProfile()}
        title="Profile Error"
      />

      <Card
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {/* Profile Avatar */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 4, pb: 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                fontSize: '2rem',
                fontWeight: 700,
                boxShadow: `0 4px 16px ${theme.palette.primary.main}30`,
              }}
            >
              {name ? name.charAt(0).toUpperCase() : <PersonIcon sx={{ fontSize: '2.5rem' }} />}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                Profile Picture
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Your avatar displays the first letter of your name
              </Typography>
            </Box>
          </Box>

          {/* Name Field */}
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Basic Information
          </Typography>
          
          <ModernTextField
            fullWidth
            label="Full Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={!editing}
            required
            placeholder="Enter your full name"
            inputProps={{ maxLength: 100 }}
            helperText={editing ? `${name.length}/100 characters` : ""}
          />

          {/* Placeholder for future fields */}
          {/* Future fields like bio, phone number, etc. can be added here */}
        </CardContent>
      </Card>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        message={success}
      />
    </Box>
  );
};

export default PersonalInfoSection; 